import { QueryClient } from '@tanstack/react-query';

/**
 * App-wide caching policy: there is none.
 *
 * Every query is stale the moment it resolves (`staleTime: 0`) and its cache entry is
 * dropped the moment its last observer unmounts (`gcTime: 0`). Opening any page therefore
 * always shows a loading state and then server-fresh data — including navigating back to a
 * page visited seconds ago. The flicker that follows from that is the accepted cost.
 *
 * The `refetchOn*` flags are written out even where they match the library default: this
 * file is the single place the policy is declared, and a reader must not have to know
 * those defaults to understand it. `refetchOnWindowFocus` is the one that genuinely
 * changes behaviour — the Android Capacitor build loads a remote URL, so the WebView is a
 * long-lived process and app-resume fires a focus event.
 *
 * Caching is disabled here and nowhere else: do not set `staleTime` or `gcTime` on an
 * individual query, ESLint rejects it. `refetchInterval` on an individual query is still
 * allowed — that is polling, not caching.
 *
 * Note this covers only the query cache. The HTTP layer is handled separately: the backend
 * sends `no-store` on `/api/*` (server.ts) and the axios client asks for `no-cache`
 * (lib/api.ts), because a WebView can otherwise answer a fresh request from its own disk.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: 'always',
      // Retry is about transient network failure, not caching. It matters more now, not
      // less: with nothing cached, a failed request means an error screen rather than a
      // stale copy.
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});
